import React, { useEffect, useState } from "react";
import { Row, Col, Input, Table, Pagination } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import Label from "../Label/Label";
import Localize, { l } from "../../libs/localization";
import { searchBrandAdvertisersApi } from "../../api/frontend.api";
import GatedTableEmptyState from "../../design-library/GatedTableEmptyState/GatedTableEmptyState.component";

const BrandAdvertisersTable = ({
  filters,
  onFiltersChange,
  hasAccess = true,
}) => {
  const [pricePerImpression, setPricePerImpression] = useState(0.007);

  const advertisersQuery = useQuery({
    queryKey: ["brandAdvertisers", filters],
    queryFn: () => {
      return searchBrandAdvertisersApi({ filters });
    },
    enabled: hasAccess,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const data = [];
  if (advertisersQuery.data?.results)
    advertisersQuery.data.results.forEach((item, i) => {
      data.push({
        key: i,
        type: item.advertiserType,
        company: {
          name: item.brand.company?.name,
          id: item.brand.company?.id,
        },
        brand: {
          name: item.brand.name,
          id: item.brand._id,
          companyId: item.brand.company?.id,
        },
        year: item.year,
        payer: item.payer,
        beneficiary: item.beneficiary,
        ads: item.n_ads,
        raw: item,
        reach: (() => {
          switch (item.advertiserType) {
            case "META":
            default:
              return item.eu_total_reach?.toLocaleString();
            case "GOOGLE":
              return `${item.estimated_times_shown_lower_bound?.toLocaleString()} - ${item.estimated_times_shown_upper_bound?.toLocaleString()}`;
          }
        })(),
        estimatedSpending: (() => {
          switch (item.advertiserType) {
            case "META":
            default:
              return item.eu_total_reach * pricePerImpression;
            case "GOOGLE":
              return (
                ((item.estimated_times_shown_lower_bound +
                  item.estimated_times_shown_upper_bound) /
                  2) *
                pricePerImpression
              );
          }
        })(),
        libraryUrl: (() => {
          switch (item.advertiserType) {
            case "META":
            default:
              return `https://www.facebook.com/ads/library/?id=${item.advertiser.metaLibraryId}`;
            case "GOOGLE":
              return `https://adstransparency.google.com/advertiser/${item.advertiser.googleAdvertiserId}`;
          }
        })(),
      });
    });

  const eur = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  });

  const columns = [
    {
      title: l("COMMON.COMPANY"),
      dataIndex: "company",
      key: "company",
      render: ({ id, name }) => <Link to={`/companies/${id}`}>{name}</Link>,
    },
    {
      title: l("COMMON.BRAND"),
      dataIndex: "brand",
      key: "brand",
      render: ({ id, name, companyId }) => (
        <Link to={`/companies/${companyId}?brand=${id}`}>{name}</Link>
      ),
    },
    {
      title: l("BRAND_ADVERTISERS.PLATFORM"),
      dataIndex: "type",
      key: "type",
      render: (type) => {
        switch (type) {
          case "META":
          default:
            return l("BRAND_ADVERTISERS.PLATFORM_META");
          case "GOOGLE":
            return l("BRAND_ADVERTISERS.PLATFORM_GOOGLE");
        }
      },
    },
    {
      title: l("COMMON.YEAR"),
      dataIndex: "year",
      key: "year",
    },
    {
      title: l("BRAND_ADVERTISERS.N_ADS"),
      dataIndex: "ads",
      key: "ads",
    },
    {
      title: l("PAGES.SEARCH.REACH"),
      dataIndex: "reach",
      key: "reach",
    },
    {
      title: l("BRAND_ADVERTISERS.ESTIMATED_SPENDING_FORMULA"),
      dataIndex: "estimatedSpending",
      key: "estimatedSpending",
      render: (estimatedSpending) => eur.format(estimatedSpending),
    },
    {
      title: l("BRAND_ADVERTISERS.ADL_URL"),
      dataIndex: "libraryUrl",
      key: "libraryUrl",
      render: (libraryUrl, row) => (
        <a href={libraryUrl} target="_blank" rel="noreferrer">
          {(() => {
            switch (row.type) {
              default:
              case "META": {
                return l("BRAND_ADVERTISERS.OPEN_META");
              }
              case "GOOGLE": {
                return l("BRAND_ADVERTISERS.OPEN_GOOGLE");
              }
            }
          })()}
        </a>
      ),
    },
  ];

  const pagination = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        margin: "16px 0",
      }}
    >
      <Pagination
        total={advertisersQuery.data?.pageInfo?.total}
        current={filters.page + 1}
        onChange={(page, pageSize) =>
          onFiltersChange({ ...filters, page: page - 1, pageSize })
        }
        pageSize={filters.pageSize}
        pageSizeOptions={["10", "20", "50", "100"]}
      />
    </div>
  );

  return (
    <div>
      <Row justify={"space-between"} style={{ marginBottom: 16 }}>
        <Col>
          <Label
            className="filter-color"
            value={Localize("BRAND_ADVERTISERS.COST_PER_REACH")}
          />
          <Input
            type="number"
            value={pricePerImpression}
            onChange={(e) => {
              setPricePerImpression(e.target.value);
            }}
          />
        </Col>
        <Col>{pagination}</Col>
      </Row>

      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        loading={advertisersQuery.isFetching}
        locale={
          !hasAccess
            ? {
                emptyText: (
                  <GatedTableEmptyState
                    message={Localize(
                      "PAGES.COMPANIES.DETAIL.SECTIONS.DIGITAL_INVESTMENTS_GATED_MESSAGE",
                    )}
                  />
                ),
              }
            : undefined
        }
        expandable={{
          rowExpandable: () => true,
          expandedRowRender: (r) => {
            const row = r.raw;
            const mrows = row.beneficiaries?.map((beneficiary, i) => ({
              beneficiary: beneficiary,
              payer: row.payers[i],
              ads: row.n_ads_values[i],
              reach: (() => {
                switch (row.advertiserType) {
                  case "META":
                  default:
                    return row.eu_total_reach_values[i]?.toLocaleString();
                  case "GOOGLE":
                    return `${row.estimated_times_shown_lower_bound_values[
                      i
                    ]?.toLocaleString()} - ${row.estimated_times_shown_upper_bound_values[
                      i
                    ]?.toLocaleString()}`;
                }
              })(),
              estimatedSpending: (() => {
                switch (row.advertiserType) {
                  case "META":
                  default:
                    return row.eu_total_reach_values[i] * pricePerImpression;
                  case "GOOGLE":
                    return (
                      ((row.estimated_times_shown_lower_bound_values[i] +
                        row.estimated_times_shown_upper_bound_values[i]) /
                        2) *
                      pricePerImpression
                    );
                }
              })(),
              surfaces: row.surfaces_values?.[i] || {},
            }));

            const baseColumns = [
              {
                title: l("PAGES.SEARCH.PAYER"),
                dataIndex: "payer",
                key: "payer",
              },
              {
                title: l("BRAND_ADVERTISERS.BENEFICIARY"),
                dataIndex: "beneficiary",
                key: "beneficiary",
              },
              {
                title: l("BRAND_ADVERTISERS.N_ADS"),
                dataIndex: "ads",
                key: "ads",
              },
              {
                title: l("PAGES.SEARCH.REACH"),
                dataIndex: "reach",
                key: "reach",
              },
            ];

            const surfaceColumns =
              row.advertiserType === "GOOGLE"
                ? [
                    {
                      title: "Search",
                      dataIndex: ["surfaces", "SEARCH"],
                      key: "search",
                      render: (surface) => {
                        if (surface?.times_shown_lower_bound === undefined)
                          return "-";
                        if (
                          surface.times_shown_lower_bound === 0 &&
                          surface.times_shown_upper_bound === 0
                        )
                          return "0";
                        return `${surface.times_shown_lower_bound?.toLocaleString()} - ${surface.times_shown_upper_bound?.toLocaleString()}`;
                      },
                    },
                    {
                      title: "Maps",
                      dataIndex: ["surfaces", "MAPS"],
                      key: "maps",
                      render: (surface) => {
                        if (surface?.times_shown_lower_bound === undefined)
                          return "-";
                        if (
                          surface.times_shown_lower_bound === 0 &&
                          surface.times_shown_upper_bound === 0
                        )
                          return "0";
                        return `${surface.times_shown_lower_bound?.toLocaleString()} - ${surface.times_shown_upper_bound?.toLocaleString()}`;
                      },
                    },
                    {
                      title: "Shopping",
                      dataIndex: ["surfaces", "SHOPPING"],
                      key: "shopping",
                      render: (surface) => {
                        if (surface?.times_shown_lower_bound === undefined)
                          return "-";
                        if (
                          surface.times_shown_lower_bound === 0 &&
                          surface.times_shown_upper_bound === 0
                        )
                          return "0";
                        return `${surface.times_shown_lower_bound?.toLocaleString()} - ${surface.times_shown_upper_bound?.toLocaleString()}`;
                      },
                    },
                    {
                      title: "YouTube",
                      dataIndex: ["surfaces", "YOUTUBE"],
                      key: "youtube",
                      render: (surface) => {
                        if (surface?.times_shown_lower_bound === undefined)
                          return "-";
                        if (
                          surface.times_shown_lower_bound === 0 &&
                          surface.times_shown_upper_bound === 0
                        )
                          return "0";
                        return `${surface.times_shown_lower_bound?.toLocaleString()} - ${surface.times_shown_upper_bound?.toLocaleString()}`;
                      },
                    },
                    {
                      title: "Play",
                      dataIndex: ["surfaces", "PLAY"],
                      key: "play",
                      render: (surface) => {
                        if (surface?.times_shown_lower_bound === undefined)
                          return "-";
                        if (
                          surface.times_shown_lower_bound === 0 &&
                          surface.times_shown_upper_bound === 0
                        )
                          return "0";
                        return `${surface.times_shown_lower_bound?.toLocaleString()} - ${surface.times_shown_upper_bound?.toLocaleString()}`;
                      },
                    },
                  ]
                : [];

            const estimatedSpendingColumn = {
              title: l("BRAND_ADVERTISERS.ESTIMATED_SPENDING_FORMULA"),
              dataIndex: "estimatedSpending",
              key: "estimatedSpending",
              render: (estimatedSpending) => eur.format(estimatedSpending),
            };

            return (
              <Table
                dataSource={mrows}
                columns={[
                  ...baseColumns,
                  ...surfaceColumns,
                  estimatedSpendingColumn,
                ]}
                pagination={false}
              ></Table>
            );
          },
        }}
      />
      {pagination}
    </div>
  );
};

export default BrandAdvertisersTable;
